import React, { useState, useEffect, useMemo, Suspense } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Badge, Button, Drawer } from 'antd';
import {
    LayoutDashboard,
    Landmark,
    CircleDollarSign,
    Wallet,
    Package,
    BarChart3,
    Settings,
    Shield,
    Bell,
    LogOut,
    User,
    Headphones,
    Grid3x3,
    UserCog,
    BookOpen,
    Receipt,
    Printer,
    Building2,
    Menu as MenuIcon,
    X,
} from 'lucide-react';
import { useAuth } from '../../App';
import TenantSwitcher, { getActiveTenantName } from './TenantSwitcher';
import { PageFallback } from '../../routes/lazyPages';
import { APP_NAV, filterNavByPermissions } from '../../config/navigation';

const { Sider, Header, Content } = Layout;

const DESKTOP_BREAKPOINT = 992;

const ICON_MAP = {
    LayoutDashboard,
    Landmark,
    CircleDollarSign,
    Wallet,
    Package,
    BarChart3,
    Settings,
    Shield,
    UserCog,
    BookOpen,
    Receipt,
    Printer,
    Building2,
};

const iconSize = 18;

function buildMenuItems(nav, permissions, isSuperAdmin) {
    const items = filterNavByPermissions(nav, permissions, isSuperAdmin).map((item) => {
        const Icon = ICON_MAP[item.icon] || LayoutDashboard;
        if (item.children) {
            return {
                key: item.key,
                icon: <Icon size={iconSize} strokeWidth={2} />,
                label: item.label,
                children: item.children.map((child) => ({
                    key: child.path,
                    label: child.label,
                })),
            };
        }
        return {
            key: item.path,
            icon: <Icon size={iconSize} strokeWidth={2} />,
            label: item.label,
        };
    });

    if (isSuperAdmin) {
        items.push({
            key: '/super-admin',
            icon: <Shield size={iconSize} strokeWidth={2} />,
            label: 'Companies',
        });
    }

    if (permissions.includes('all') || permissions.includes('users')) {
        items.push({
            key: '/users',
            icon: <UserCog size={iconSize} strokeWidth={2} />,
            label: 'Users',
        });
    }

    return items;
}

function getSelectedKeys(pathname) {
    if (pathname.startsWith('/bank')) return ['/bank/accounts'].includes(pathname) ? [pathname] : [pathname];
    return [pathname];
}

function getOpenKeys(pathname) {
    if (pathname.startsWith('/bank')) return ['bank'];
    if (pathname.startsWith('/sales') || pathname === '/customers') return ['sales'];
    if (pathname.startsWith('/purchases')) return ['purchases'];
    if (pathname === '/products' || pathname.startsWith('/inventory')) return ['inventory'];
    if (pathname.startsWith('/accounting')) return ['accounting'];
    if (pathname.startsWith('/tax')) return ['tax'];
    if (pathname.startsWith('/printing')) return ['printing'];
    if (pathname.startsWith('/enterprise')) return ['enterprise'];
    return [];
}

const EMPTY_PERMISSIONS = [];

const MainLayout = () => {
    const [collapsed, setCollapsed] = useState(true);
    const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= DESKTOP_BREAKPOINT);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [openKeys, setOpenKeys] = useState([]);
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout, isSuperAdmin, permissions: authPermissions } = useAuth();

    const permissions = authPermissions?.length ? authPermissions : EMPTY_PERMISSIONS;
    const menuItems = useMemo(
        () => buildMenuItems(APP_NAV, permissions, isSuperAdmin),
        [permissions, isSuperAdmin]
    );

    useEffect(() => {
        document.documentElement.removeAttribute('data-theme');
        localStorage.removeItem('upos_theme');
    }, []);

    useEffect(() => {
        const onResize = () => {
            const desktop = window.innerWidth >= DESKTOP_BREAKPOINT;
            setIsDesktop(desktop);
            if (desktop) {
                setMobileNavOpen(false);
            } else {
                setCollapsed(true);
            }
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const handleSiderEnter = () => {
        if (!isDesktop) return;
        setCollapsed(false);
        setOpenKeys(getOpenKeys(location.pathname));
    };

    const handleSiderLeave = () => {
        if (!isDesktop) return;
        setCollapsed(true);
    };

    useEffect(() => {
        setOpenKeys(getOpenKeys(location.pathname));
        setMobileNavOpen(false);
    }, [location.pathname]);

    const userMenuItems = [
        { key: 'profile', icon: <User size={14} />, label: 'Profile', onClick: () => navigate('/profile') },
        { key: 'settings', icon: <Settings size={14} />, label: 'Settings', onClick: () => navigate('/settings') },
        { type: 'divider' },
        { key: 'logout', icon: <LogOut size={14} />, label: 'Logout', danger: true, onClick: logout },
    ];

    const navMenu = (
        <Menu
            mode="inline"
            selectedKeys={getSelectedKeys(location.pathname)}
            openKeys={openKeys}
            onOpenChange={setOpenKeys}
            onClick={({ key }) => {
                navigate(key);
                setMobileNavOpen(false);
            }}
            items={menuItems}
            className="app-sider-menu"
        />
    );

    const contentFitClass = location.pathname === '/'
        ? ' app-content-v2--dashboard-fit'
        : (
            /\/sales\/(invoices\/new|receipts\/(new|\d+\/edit))/.test(location.pathname)
            || /\/customers\/(new|\d+\/(edit|payment))/.test(location.pathname)
            || /\/purchases\/(bills\/new|payments\/new)/.test(location.pathname)
            || /\/products\/(new|\d+\/edit)/.test(location.pathname)
            || /\/inventory\/(adjustments\/new|transfers\/new|batches\/new)/.test(location.pathname)
        )
            ? ' app-content-v2--enterprise-fit'
            : '';

    return (
        <Layout className="app-shell">
            <Header className="app-topbar">
                <div className="app-topbar-left">
                    {!isDesktop && (
                        <Button
                            type="text"
                            className="app-topbar-icon app-topbar-menu-btn"
                            aria-label={mobileNavOpen ? 'Close navigation' : 'Open navigation'}
                            icon={mobileNavOpen ? <X size={20} /> : <MenuIcon size={20} />}
                            onClick={() => setMobileNavOpen((open) => !open)}
                        />
                    )}
                    <div className="app-brand-mark">UP</div>
                    {isDesktop && (
                        <Button type="text" className="app-topbar-icon" icon={<Grid3x3 size={18} />} />
                    )}
                    <TenantSwitcher user={user} />
                </div>
                <div className="app-topbar-right">
                    <Button type="text" className="app-topbar-link">My Tasks</Button>
                    <Button type="text" className="app-topbar-link" icon={<Headphones size={16} />}>Support</Button>
                    <Badge count={0} size="small" showZero={false}>
                        <Button type="text" className="app-topbar-icon" icon={<Bell size={18} />} />
                    </Badge>
                    <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                        <button type="button" className="app-topbar-user">
                            <Avatar size={28} style={{ background: '#fff', color: '#1D4ED8' }} icon={<User size={14} />} />
                        </button>
                    </Dropdown>
                    <Button
                        type="text"
                        className="app-topbar-icon"
                        icon={<Settings size={18} />}
                        onClick={() => navigate('/settings')}
                    />
                </div>
            </Header>

            <Layout className="app-body">
                {isDesktop ? (
                    <Sider
                        collapsible
                        collapsed={collapsed}
                        onCollapse={setCollapsed}
                        trigger={null}
                        width={240}
                        collapsedWidth={64}
                        className="app-sider-v2"
                        theme="light"
                        onMouseEnter={handleSiderEnter}
                        onMouseLeave={handleSiderLeave}
                    >
                        <div className="app-sider-brand">
                            {!collapsed && (
                                <>
                                    <span className="app-sider-brand-name">UniversalPOS</span>
                                    <span className="app-sider-brand-co">{getActiveTenantName() || user?.businessName}</span>
                                </>
                            )}
                        </div>
                        <Menu
                            mode="inline"
                            selectedKeys={getSelectedKeys(location.pathname)}
                            openKeys={collapsed ? [] : openKeys}
                            onOpenChange={setOpenKeys}
                            onClick={({ key }) => navigate(key)}
                            items={menuItems}
                            className="app-sider-menu"
                        />
                    </Sider>
                ) : null}

                <Drawer
                    placement="left"
                    open={!isDesktop && mobileNavOpen}
                    onClose={() => setMobileNavOpen(false)}
                    width={Math.min(280, typeof window !== 'undefined' ? window.innerWidth - 48 : 280)}
                    className="app-nav-drawer"
                    styles={{ body: { padding: 0 } }}
                    title={
                        <div className="app-sider-brand app-sider-brand--drawer">
                            <span className="app-sider-brand-name">UniversalPOS</span>
                            <span className="app-sider-brand-co">{getActiveTenantName() || user?.businessName}</span>
                        </div>
                    }
                >
                    {navMenu}
                </Drawer>

                <Content className={`app-content-v2${contentFitClass}`}>
                    <Suspense fallback={<PageFallback />}>
                        <Outlet />
                    </Suspense>
                </Content>
            </Layout>
        </Layout>
    );
};

export default MainLayout;
